/**
 * Customer 360 — horizontal tab nav.
 *
 * Collapses to a single `<select>` below `lg:` (mobile / tablet) so the tab
 * row never wraps. Driven by props so the parent controls state.
 */
import { ChevronDown } from 'lucide-react';

export type CustomerTab =
  | 'overview'
  | 'pets'
  | 'bookings'
  | 'photos'
  | 'messages'
  | 'payments'
  | 'loyalty'
  | 'vaccines'
  | 'notes'
  | 'audit';

interface TabDef {
  id: CustomerTab;
  label: string;
  count?: number;
  hidden?: boolean;
}

interface Props {
  active: CustomerTab;
  tabs: TabDef[];
  onChange: (id: CustomerTab) => void;
}

export function Customer360Tabs({ active, tabs, onChange }: Props) {
  const visible = tabs.filter((t) => !t.hidden);
  return (
    <>
      {/* Desktop: tab row */}
      <div
        className="hidden lg:flex border-b border-border overflow-x-auto"
        data-testid="customer360-tabs"
      >
        {visible.map((tab) => {
          const isActive = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={[
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap press',
                isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              ].join(' ')}
              data-testid={`customer360-tab-${tab.id}`}
            >
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span className="num rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Mobile + tablet: dropdown */}
      <div className="lg:hidden border-b border-border pb-3">
        <label htmlFor="customer360-tab-select" className="sr-only">
          Section
        </label>
        <div className="relative">
          <select
            id="customer360-tab-select"
            value={active}
            onChange={(e) => onChange(e.target.value as CustomerTab)}
            className="h-11 w-full appearance-none rounded-md border border-border bg-surface px-3 pr-9 text-sm font-medium outline-none focus:ring-2 focus:ring-ring/30"
          >
            {visible.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.label}
                {tab.count != null && tab.count > 0 ? ` (${tab.count})` : ''}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.75}
          />
        </div>
      </div>
    </>
  );
}
